import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Activity, Database, LayoutDashboard, Map, Radio, Settings, UserPlus,
  Grape, Search, LogOut, UserRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Resumen", icon: LayoutDashboard, testId: "nav-overview", end: true },
  { to: "/sensores", label: "Sensores", icon: Radio, testId: "nav-sensors" },
  { to: "/mapa", label: "Mapa", icon: Map, testId: "nav-map" },
  { to: "/admin/onboarding", label: "Onboarding", icon: UserPlus, testId: "nav-admin-onboarding", adminOnly: true },
  { to: "/admin/mildew", label: "Diagnóstico de mildiu", icon: Activity, testId: "nav-admin-mildew", adminOnly: true },
  { to: "/admin/explorer", label: "Explorador de datos", icon: Database, testId: "nav-admin-explorer", adminOnly: true },
  { to: "/ajustes", label: "Ajustes", icon: Settings, testId: "nav-settings" },
];

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "platform_admin";
  const displayName = user?.display_name || "Cliente";
  const initials = displayName.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="h-screen overflow-hidden flex bg-page text-ink">
      {/* Sidebar */}
      <aside className="w-[264px] shrink-0 bg-brand text-white flex flex-col fixed h-screen z-20" data-testid="sidebar">
        <div className="px-6 h-[68px] flex items-center gap-2.5 border-b border-white/10">
          <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center">
            <Grape size={20} className="text-white" strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-lg tracking-tight">Treeplet</div>
            <div className="text-[11px] text-white/60 tracking-widest uppercase">Centro de Operaciones</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 tp-scroll overflow-y-auto">
          {NAV.filter((item) => isAdmin ? item.adminOnly : !item.adminOnly).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200",
                    isActive
                      ? "bg-white/12 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/8"
                  )
                }
              >
                <Icon size={18} strokeWidth={2} />
                <span>{item.label}</span>
                {item.to === "/alertas" && activeAlerts.length > 0 && (
                  <span className="ml-auto text-[11px] font-semibold bg-critical text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                    {activeAlerts.length}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="rounded-lg bg-white/6 p-3">
            <div className="flex items-center gap-2 text-white/70 text-[11px] uppercase tracking-widest mb-1">
              <div className="w-1.5 h-1.5 rounded-full bg-stable animate-pulse" />
              Sistema operativo
            </div>
            <div className="text-xs text-white/80">{isAdmin ? "Gestión y diagnósticos de plataforma" : "Datos actualizados cada 60 segundos"}</div>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 ml-[264px] flex flex-col h-screen overflow-hidden">
        {/* Top bar */}
        <header className="h-[68px] shrink-0 bg-surface border-b border-line flex items-center px-8 gap-6 z-10" data-testid="topbar">
          {!isAdmin && <div className="flex-1 max-w-md relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
            <input
              data-testid="global-search"
              placeholder="Buscar viñedo, sensor o alerta…"
              className="w-full h-10 pl-9 pr-3 rounded-md bg-page border border-line text-sm outline-none focus:border-brand transition-colors"
            />
          </div>}

          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right leading-tight">
              <div className="text-sm font-semibold text-ink">{displayName}</div>
              <div className="text-[11px] text-ink-tertiary">{user?.tenant?.name}</div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center font-heading font-bold text-sm hover:bg-brand-hover transition-colors outline-none"
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-2">
                    <UserRound size={15} className="text-brand" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{displayName}</div>
                      <div className="text-[11px] text-ink-tertiary truncate font-normal">{user?.email}</div>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!isAdmin && <DropdownMenuItem onClick={() => navigate("/ajustes")} data-testid="user-menu-settings">
                  <Settings size={15} className="mr-2" /> Ajustes
                </DropdownMenuItem>}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-critical focus:text-critical">
                  <LogOut size={15} className="mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-hidden p-6" data-testid="main-workspace">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
